import {
  DataGrid,
  formatDate,
  formatJson,
  Pre,
  StatusBadge,
  type Severity
} from '@ijoin/components';
import { SearchActivityRes, useGetEnrollmentActivity } from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material';

import { FC, useState } from 'react';

const ACTIVITY_STATUS_SEVERITY: Record<string, Severity> = {
  C: 'success',
  E: 'error',
  S: 'success',
  W: 'warning'
};

export const EnrollmentActivitySection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const [selected, setSelected] = useState<null | SearchActivityRes>(null);

  const activity = useGetEnrollmentActivity(
    props.planId,
    props.planEnrollmentId,
    {
      take: 500
    }
  );

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader title='Activity' />
      <CardContent>
        <DataGrid
          columns={[
            {
              align: 'center',
              disableColumnMenu: true,
              display: 'flex',
              field: 'actions',
              filterable: false,
              headerName: '',
              renderCell: params => (
                <IconButton
                  aria-label='Details'
                  data-testid={`details-${params.row.jobId}`}
                  onClick={() => setSelected(params.row)}>
                  <InfoOutlinedIcon />
                </IconButton>
              ),
              sortable: false,
              width: 65
            },
            {
              field: 'taskType',
              flex: 1,
              headerName: 'Task Type'
            },
            {
              field: 'status',
              flex: 0.5,
              headerName: 'Status',
              renderCell: params => (
                <StatusBadge
                  severity={
                    ACTIVITY_STATUS_SEVERITY[params.row.statusCode] ?? 'info'
                  }>
                  {params.value}
                </StatusBadge>
              )
            },
            {
              field: 'queueDateTime',
              flex: 0.75,
              headerName: 'Queued',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'jobStartDateTime',
              flex: 0.75,
              headerName: 'Started',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            },
            {
              field: 'jobEndDateTime',
              flex: 0.75,
              headerName: 'Ended',
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' })
                  : ''
            }
          ]}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.jobId}
          loading={activity.isFetching}
          pageSizeOptions={[25, 50, 100]}
          rows={activity.data?.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'enrollmentActivity'
            }
          }}
        />

        <Dialog
          fullWidth
          maxWidth='md'
          onClose={() => setSelected(null)}
          open={!!selected}>
          <DialogTitle
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            <Typography component='span' variant='h4'>
              {selected?.taskType}
            </Typography>
            <IconButton onClick={() => setSelected(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom variant='overline'>
              Job Info
            </Typography>
            <Paper variant='outlined'>
              <Pre>
                {selected?.jobBody
                  ? formatJson(selected.jobBody)
                  : 'No job info'}
              </Pre>
            </Paper>

            <Typography gutterBottom sx={{ mt: 3 }} variant='overline'>
              Log Info
            </Typography>
            <Paper variant='outlined'>
              <Pre>
                {selected?.logInfo
                  ? formatJson(selected.logInfo)
                  : 'No log info'}
              </Pre>
            </Paper>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

EnrollmentActivitySection.displayName = 'EnrollmentActivitySection';
